import { useCallback } from 'react';
import { type OpenApiSchemaAlert } from '#shared/api/schema';
import {
    AUTOMATIC_PRESENTATIONS,
    type AutomaticPresentationEligibility,
    useAutomaticPresentation,
} from '#shared/lib/automaticPresentation';
import { useDashboardAlertsCooldown } from './useDashboardAlertsCooldown';

function getEligibility(
    alerts: readonly OpenApiSchemaAlert[] | undefined,
    criticalAlerts: readonly OpenApiSchemaAlert[],
    hasAlertReadyToShow: boolean,
    isSuppressed: boolean
): AutomaticPresentationEligibility {
    if (isSuppressed) {
        return 'ineligible';
    }

    if (alerts === undefined) {
        return 'pending';
    }

    if (criticalAlerts.length === 0) {
        return 'ineligible';
    }

    return hasAlertReadyToShow ? 'eligible' : 'ineligible';
}

type UseDashboardAlertsPresentationResult = Readonly<{
    criticalAlerts: readonly OpenApiSchemaAlert[];
    isAutomaticallyOpen: boolean;
    dismissAutomaticPresentation: VoidFunction;
    startCriticalAlertsCooldown: VoidFunction;
}>;

/**
 * Управляет автопоказом критичных алертов и их трёхчасовым cooldown.
 * Время следующего показа сохраняется при закрытии отдельно для каждого появления алерта (`id + created_at`),
 * поэтому новый алерт или повторное возникновение устранённой ошибки показываются сразу.
 *
 * @kind helper
 * @param alerts Загруженные активные алерты; `undefined` означает, что запрос ещё не завершён.
 * @param isSuppressed Не запускать автопоказ, например, пока окно алертов открыто вручную.
 * @returns Критичные алерты и методы управления автопоказом.
 */
export function useDashboardAlertsPresentation(
    alerts: readonly OpenApiSchemaAlert[] | undefined,
    isSuppressed = false
): UseDashboardAlertsPresentationResult {
    const { criticalAlerts, hasAlertReadyToShow, startCriticalAlertsCooldown } = useDashboardAlertsCooldown(alerts);
    const eligibility = getEligibility(alerts, criticalAlerts, hasAlertReadyToShow, isSuppressed);

    const { isActive, isLeaving, startLeaving, completeLeaving } = useAutomaticPresentation({
        ...AUTOMATIC_PRESENTATIONS.HIGH.dashboardCriticalAlerts,
        eligibility,
        debug: {
            label: 'Критичные алерты',
            owner: 'DashboardAlerts',
            surface: 'Popup',
        },
    });

    const dismissAutomaticPresentation = useCallback(() => {
        const wasActive = startLeaving('dismissed');

        if (wasActive) {
            startCriticalAlertsCooldown();
            // Popup закрывается сразу и не предоставляет отдельный lifecycle завершения анимации.
            completeLeaving();
        }
    }, [completeLeaving, startCriticalAlertsCooldown, startLeaving]);

    return {
        criticalAlerts,
        isAutomaticallyOpen: isActive || isLeaving,
        dismissAutomaticPresentation,
        startCriticalAlertsCooldown,
    };
}
